import React, { useState, useEffect } from 'react';
import userService from '../api/userService';
import { useAuth } from '../hooks/useAuth';
import Modal from '../components/Modal';
import UserForm from '../components/UserForm';
import Button from '../components/Button';
import RowActions from '../components/RowActions';
// Estados de carga/error compartidos (ver hallazgo de auditoría #08)
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { ROLE_LABELS } from '../utils/roles';

/**
 * @description Página de gestión de usuarios.
 * ADMIN puede crear, editar y eliminar.
 * SUPERVISOR solo puede ver la lista y filtrar por rol.
 * @returns {JSX.Element}
 */
const Users = () => {
  const { currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Error de carga inicial — bloquea el render de la tabla
  const [loadError, setLoadError] = useState('');
  // Error de operación (guardar/eliminar) — se muestra encima de la tabla sin bloquear
  const [actionError, setActionError] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentEditUser, setCurrentEditUser] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);

  // Paginación
  const LIMIT = 20;
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  // El rol del usuario activo determina qué acciones son visibles
  const isAdmin = currentUser?.role === 'ADMIN';

  useEffect(() => {
    fetchUsers();
  }, []);

  /**
   * @description Muestra un error de operación y lo limpia automáticamente tras 6 segundos.
   * @param {string} message - Mensaje de error a mostrar.
   */
  const showActionError = (message) => {
    // Los errores de class-validator llegan como array de mensajes
    setActionError(Array.isArray(message) ? message.join(' ') : message);
    setTimeout(() => setActionError(''), 6000);
  };

  /**
   * @description Carga la lista completa de usuarios desde la API.
   */
  const fetchUsers = async (targetPage = 1) => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await userService.getUsers({ page: targetPage, limit: LIMIT });
      setUsers(response.data.data);
      setMeta(response.data.meta);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setLoadError('No se pudo cargar la lista de usuarios. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Maneja el guardado de un usuario: crea uno nuevo o actualiza el existente.
   * La creación usa POST /users; la edición usa PATCH /users/:id y, si cambió el rol,
   * PATCH /users/:id/role (el rol no se acepta en el PATCH general).
   * @param {object} userData - Datos provenientes del formulario.
   */
  const handleSave = async (userData) => {
    try {
      if (currentEditUser) {
        // Edición: PATCH /users/:id (solo ADMIN llega aquí)
        const { role, ...profileData } = userData;
        await userService.updateUser(currentEditUser.user_id, profileData);
        if (role && role !== currentEditUser.role) {
          await userService.changeUserRole(currentEditUser.user_id, role);
        }
      } else {
        // Creación: POST /users
        await userService.createUser(userData);
      }
      await fetchUsers();
      closeModal();
    } catch (err) {
      console.error('Error al guardar usuario:', err);
      const message = err.response?.data?.message || 'No se pudo guardar el usuario.';
      showActionError(message);
    }
  };

  /**
   * @description Maneja la eliminación de un usuario tras confirmación explícita.
   * Solo disponible para ADMIN.
   * @param {number} userId - ID del usuario a eliminar.
   * @param {string} username - Nombre de usuario (para el mensaje de confirmación).
   */
  const handleDelete = (userId, username) => {
    setConfirmDeleteUser({ userId, username });
  };

  const executeDelete = async () => {
    const { userId } = confirmDeleteUser;
    setConfirmDeleteUser(null);
    try {
      await userService.deleteUser(userId);
      await fetchUsers();
    } catch (err) {
      console.error('Error al eliminar usuario:', err);
      showActionError(err.response?.data?.message || 'No se pudo eliminar el usuario.');
    }
  };

  /**
   * @description Abre el modal para crear o editar un usuario.
   * @param {object | null} user - Usuario a editar, o null para crear uno nuevo.
   */
  const openModal = (user = null) => {
    setCurrentEditUser(user);
    setShowModal(true);
  };

  /**
   * @description Cierra el modal y limpia el estado de edición.
   */
  const closeModal = () => {
    setCurrentEditUser(null);
    setShowModal(false);
  };

  // Estado del campo de búsqueda por texto libre
  const [searchText, setSearchText] = useState('');

  // Filtrado en memoria: combina búsqueda por texto y filtro por rol
  const filteredUsers = users.filter((u) => {
    const matchesRole = roleFilter ? u.role === roleFilter : true;
    const query = searchText.toLowerCase();
    const matchesSearch =
      query === '' ||
      u.username?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query) ||
      u.phone?.toLowerCase().includes(query) ||
      u.position?.toLowerCase().includes(query);
    return matchesRole && matchesSearch;
  });

  // Estado de carga inicial
  if (loading) return <LoadingState message="Cargando usuarios..." />;

  // Error de carga inicial: no tiene sentido mostrar la tabla sin datos
  if (loadError) return <ErrorState message={loadError} />;

  return (
    <div>
      <h2>Usuarios</h2>

      {/* Alerta de error de operación: visible sobre la tabla, se cierra manualmente o tras 6s */}
      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span>{actionError}</span>
          <button
            onClick={() => setActionError('')}
            style={styles.actionErrorClose}
            aria-label="Cerrar mensaje de error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Barra de acciones: búsqueda + filtro por rol + botón crear (ADMIN) */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Buscar por nombre, email, teléfono o cargo..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="">Todos los roles</option>
          <option value="ADMIN">Admin</option>
          <option value="SUPERVISOR">Supervisor</option>
          <option value="TECHNICIAN">Técnico</option>
        </select>

        {isAdmin && (
          // Acción primaria de la página: relleno sólido en teal de marca
          <Button variant="primary" onClick={() => openModal()} style={{ marginLeft: 'auto' }}>
            Crear Usuario
          </Button>
        )}
      </div>

      {/* Contador de resultados visibles */}
      <p style={styles.resultCount}>
        {filteredUsers.length} {filteredUsers.length === 1 ? 'usuario encontrado' : 'usuarios encontrados'}
      </p>

      {/* Estado de lista vacía */}
      {filteredUsers.length === 0 ? (
        <p style={styles.emptyMessage}>
          {searchText || roleFilter
            ? 'No se encontraron usuarios con los filtros aplicados.'
            : 'No hay usuarios registrados.'}
        </p>
      ) : (
        <div className="table-container">
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Usuario</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Rol</th>
              <th style={styles.th}>Teléfono</th>
              <th style={styles.th}>Cargo</th>
              <th style={styles.th}>Creado</th>
              {/* La columna de acciones solo aparece para ADMIN */}
              {isAdmin && <th style={styles.th} className="col-actions-sticky">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u.user_id} style={styles.row}>
                <td style={styles.td}>{u.username}</td>
                <td style={styles.td}>{u.email}</td>
                <td style={styles.td}>
                  <span style={getRoleBadgeStyle(u.role)}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td style={styles.td}>{u.phone || '—'}</td>
                <td style={styles.td}>{u.position || '—'}</td>
                <td style={styles.td}>
                  {u.created_at
                    ? new Date(u.created_at).toLocaleDateString('es-CL')
                    : '—'}
                </td>
                {isAdmin && (
                  <td style={styles.td} className="col-actions-sticky">
                    {/* Editar: tinte teal, acción esperada. Eliminar: solo-ícono,
                        neutro en reposo y rojo recién al hover — ver RowActions.js */}
                    <RowActions
                      onEdit={() => openModal(u)}
                      onDelete={() => handleDelete(u.user_id, u.username)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Controles de paginación */}
      {meta.totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => fetchUsers(meta.page - 1)}
            disabled={meta.page <= 1}
            style={{ ...styles.pageButton, opacity: meta.page <= 1 ? 0.4 : 1 }}
          >
            ← Anterior
          </button>
          <span style={styles.pageInfo}>
            Página {meta.page} de {meta.totalPages} &middot; {meta.total} resultado{meta.total !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => fetchUsers(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
            style={{ ...styles.pageButton, opacity: meta.page >= meta.totalPages ? 0.4 : 1 }}
          >
            Siguiente →
          </button>
        </div>
      )}

      {/* Modal compartido para creación y edición */}
      <Modal
        show={showModal}
        onClose={closeModal}
        title={currentEditUser ? 'Editar Usuario' : 'Crear Usuario'}
      >
        <UserForm
          user={currentEditUser}
          // El ADMIN no puede cambiar su propio rol (el backend responde 403)
          isSelf={currentEditUser?.user_id === currentUser?.user_id}
          onSave={handleSave}
          onCancel={closeModal}
        />
      </Modal>

      <Modal
        show={confirmDeleteUser !== null}
        onClose={() => setConfirmDeleteUser(null)}
        title="Eliminar usuario"
      >
        <p style={{ margin: 0, color: '#444' }}>
          ¿Está seguro de que desea eliminar al usuario <strong>"{confirmDeleteUser?.username}"</strong>? No podrá volver a iniciar sesión, pero su historial de actividades se conserva y su email queda libre para un usuario nuevo.
        </p>
        <div className="modal-footer">
          <button onClick={() => setConfirmDeleteUser(null)} className="modal-btn-secondary">
            Cancelar
          </button>
          <button onClick={executeDelete} className="modal-btn-danger">
            Eliminar
          </button>
        </div>
      </Modal>
    </div>
  );
};

/**
 * @description Genera el estilo del badge de rol según el valor del rol.
 * @param {string} role - Valor del rol (ADMIN, SUPERVISOR, TECHNICIAN).
 * @returns {object} Objeto de estilos inline.
 */
const getRoleBadgeStyle = (role) => {
  const colors = {
    ADMIN: { backgroundColor: '#e8f4fd', color: '#1a6496', border: '1px solid #bce0fd' },
    SUPERVISOR: { backgroundColor: '#fef9e7', color: '#856404', border: '1px solid #ffc107' },
    TECHNICIAN: { backgroundColor: '#f0f9f0', color: '#2e7d32', border: '1px solid #a5d6a7' },
  };
  return {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    ...(colors[role] || { backgroundColor: '#f0f0f0', color: '#555' }),
  };
};

// Estilos de la página
const styles = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  searchInput: {
    padding: '8px',
    minWidth: '260px',
    flex: '1',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  filterSelect: {
    padding: '8px',
    minWidth: '180px',
    border: '1px solid #ccc',
    borderRadius: '4px',
  },
  resultCount: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '8px',
  },
  // Densidad, zebra, hover y columna de acciones fija ahora viven en
  // responsive.css (.table-container table ...) — ver hallazgo de auditoría #04.
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {},
  th: {},
  row: {},
  td: {},
  emptyMessage: {
    color: '#777',
    fontStyle: 'italic',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 0',
    marginTop: '8px',
  },
  pageButton: {
    padding: '6px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#374151',
  },
  pageInfo: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  // Banner de error para operaciones fallidas (guardar, eliminar)
  actionErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fdecea',
    color: '#c0392b',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '0.9rem',
  },
  actionErrorClose: {
    background: 'none',
    border: 'none',
    color: '#c0392b',
    cursor: 'pointer',
    fontSize: '1.2rem',
    lineHeight: '1',
    padding: '0 0 0 12px',
    fontWeight: 'bold',
  },
};

export default Users;
